import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { createReference } from "@medplum/core";
import { Encounter, Observation, Patient } from "@medplum/fhirtypes";
import { useMedplum, useSubscription } from "@medplum/react";

import { LaborEncounterLiveObservationsContext } from "@/encounters/components/EncounterLabor/contexts/LaborEncounterLiveObservationsContext";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { Systems } from "@/fhir/enums/systems";
import { QuestionnaireDecimalFieldProps } from "@/intake-forms/components/QuestionnaireDecimalField";
import QuestionnaireNumberInputField from "@/intake-forms/components/QuestionnaireNumberInputField";
import { getResourcesFromBundle } from "@/utils/medplum";

import { getCumulativeBloodLossObservation } from "./cumulative-blood-loss-observation";

interface IppVitalsFormValues {
  ippVitals?: {
    estimatedBloodLossCapture?: unknown;
    estimatedBloodLossEstimated?: string | number;
    estimatedBloodLossQuantitative?: string | number;
  };
}

interface ExtendedForm {
  encounterId?: string;
  clientId?: string;
  parentFormValues?: Record<string, unknown> & IppVitalsFormValues;
  isEditingQuestionnaire?: boolean;
}

export interface BloodLossFormValues {
  estimatedBloodLossCapture: { code: string };
  estimatedBloodLossEstimated?: string | number;
  estimatedBloodLossQuantitative?: string | number;
  estimatedBloodLossCumulative?: number;
}

export default function CumulativeBloodLossField({
  form,
  name,
  label,
  formFieldName,
  questionnaireItem,
  rightSection,
  error,
  withAsterisk,
  minRange,
  maxRange,
  allowDecimal = true,
  allowNegative = true,
  fieldPath,
  "data-testid": dataTestId,
  encounterId,
  clientId,
}: Omit<QuestionnaireDecimalFieldProps<BloodLossFormValues>, "key"> & {
  fieldPath: string;
  "data-testid"?: string;
  encounterId?: string;
  clientId?: string;
}) {
  const medplum = useMedplum();
  const laborEncounterLive = useContext(LaborEncounterLiveObservationsContext);

  const extendedForm = form as ExtendedForm;
  const formContextEncounterId = extendedForm?.encounterId;
  const formContextClientId = extendedForm?.clientId;
  const isEditingQuestionnaireFromForm = Boolean(extendedForm?.isEditingQuestionnaire);

  const [cumulativeBloodLoss, setCumulativeBloodLoss] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [previousBloodLossValue, setPreviousBloodLossValue] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const didLoadRef = useRef(false);
  const hasSeededFormRef = useRef(false);

  // Use passed props, form context, or extract from URL as fallback
  const { patientId, encounterId: finalEncounterId } = useMemo(() => {
    if (encounterId && clientId) {
      return {
        patientId: clientId,
        encounterId,
      };
    }
    // Try to get from form context (for ClientFullIppAssessment)
    if (formContextEncounterId && formContextClientId) {
      return {
        patientId: formContextClientId,
        encounterId: formContextEncounterId,
      };
    }
    // Fallback to URL extraction for backward compatibility
    const pathParts = window.location.pathname.split("/");

    // Try to extract from URL pattern: /clients/{clientId}/encounters/{encounterId}/...
    const clientIndex = pathParts.findIndex((part) => part === "clients");
    const encounterIndex = pathParts.findIndex((part) => part === "encounters");

    if (clientIndex !== -1 && encounterIndex !== -1) {
      const extractedClientId = pathParts[clientIndex + 1];
      const extractedEncounterId = pathParts[encounterIndex + 1];
      return {
        patientId: extractedClientId,
        encounterId: extractedEncounterId,
      };
    }

    // Original fallback logic for other URL patterns
    return {
      patientId: pathParts[2],
      encounterId: pathParts[pathParts.length - 1],
    };
  }, [encounterId, clientId, formContextEncounterId, formContextClientId]);

  const shouldUseLaborEncounterLiveCbl = Boolean(
    laborEncounterLive &&
      finalEncounterId &&
      patientId &&
      laborEncounterLive.encounterId === finalEncounterId &&
      laborEncounterLive.clientId === patientId,
  );
  const hasAppliedLiveCumulativeRef = useRef(false);
  const restLoadRequestSequenceRef = useRef(0);
  /** Latest draft EBL for websocket merges (subscription callback must not close over a stale render). */
  const currentBloodLossRef = useRef(0);

  useEffect(() => {
    restLoadRequestSequenceRef.current += 1;
    hasAppliedLiveCumulativeRef.current = false;
  }, [patientId, finalEncounterId]);

  // Get current blood loss from form values
  // Try to get from parent form values first (for ClientFullIppAssessment), then fall back to current form
  const parentFormValues = extendedForm?.parentFormValues;
  const formValuesToUse = parentFormValues || form?.values;

  // For ClientFullIppAssessment, the values might be nested under the questionnaire group
  // Try both root level and nested level
  const rootCaptureValue = formValuesToUse?.estimatedBloodLossCapture;
  const nestedCaptureValue = (formValuesToUse as IppVitalsFormValues)?.ippVitals
    ?.estimatedBloodLossCapture;
  const captureValue = rootCaptureValue || nestedCaptureValue;

  const captureCode =
    (typeof captureValue === "object" && captureValue && "code" in captureValue
      ? (captureValue as { code?: string }).code
      : undefined) || String(captureValue);
  // 'estimated' (or "False") => use estimated field; otherwise use quantitative
  const isEstimated = captureCode === "estimated" || captureCode === "False";

  // Try both root level and nested level for the EBL values
  const rootEstimatedValue = formValuesToUse?.estimatedBloodLossEstimated;
  const nestedEstimatedValue = (formValuesToUse as IppVitalsFormValues)?.ippVitals
    ?.estimatedBloodLossEstimated;
  const rootQuantitativeValue = formValuesToUse?.estimatedBloodLossQuantitative;
  const nestedQuantitativeValue = (formValuesToUse as IppVitalsFormValues)?.ippVitals
    ?.estimatedBloodLossQuantitative;

  const currentBloodLoss = isEstimated
    ? Number(rootEstimatedValue || nestedEstimatedValue) || 0
    : Number(rootQuantitativeValue || nestedQuantitativeValue) || 0;

  useEffect(() => {
    currentBloodLossRef.current = currentBloodLoss;
  }, [currentBloodLoss]);

  // console.debug("captureValue", captureValue, { cumulativeBloodLoss, currentBloodLoss, previousBloodLossValue, isEstimated, isEditing });

  // Load existing cumulative blood loss observation and current labor stage
  useEffect(() => {
    const loadCumulativeBloodLoss = async () => {
      if (!patientId || !finalEncounterId) return;
      const requestSequence = ++restLoadRequestSequenceRef.current;

      try {
        if (!didLoadRef.current) setIsLoading(true);
        const patientRef = createReference({ id: patientId, resourceType: "Patient" } as Patient);
        const encounterRef = createReference({
          id: finalEncounterId,
          resourceType: "Encounter",
        } as Encounter);

        // Load cumulative blood loss observation
        const observation = await getCumulativeBloodLossObservation(
          medplum,
          patientRef,
          encounterRef,
        );
        if (requestSequence !== restLoadRequestSequenceRef.current) {
          return;
        }

        if (observation?.valueQuantity?.value !== undefined) {
          const pastCumulativeBloodLoss = observation.valueQuantity.value;
          const shouldApplyRestValue =
            !hasAppliedLiveCumulativeRef.current || !shouldUseLaborEncounterLiveCbl;
          if (shouldApplyRestValue) {
            setCumulativeBloodLoss(pastCumulativeBloodLoss);
          }

          // Determine editing via parent flag; capture previous entry once from form if provided
          setIsEditing(isEditingQuestionnaireFromForm);
        }
        didLoadRef.current = true;

        // Labor stage is now obtained from Redux store via selectCurrentLaborStage
      } catch (error) {
        console.error("Error loading cumulative blood loss:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCumulativeBloodLoss();
  }, [
    medplum,
    patientId,
    finalEncounterId,
    shouldUseLaborEncounterLiveCbl,
    isEditingQuestionnaireFromForm,
  ]);

  // Set the old EBL value once both cumulative and form values are loaded
  useEffect(() => {
    if (
      isEditing &&
      cumulativeBloodLoss > 0 &&
      currentBloodLoss > 0 &&
      previousBloodLossValue === 0
    ) {
      // Both values are loaded, now we can safely get the old EBL value
      const captureValue = form?.values?.estimatedBloodLossCapture;
      const captureCode =
        (typeof captureValue === "object" && captureValue && "code" in captureValue
          ? (captureValue as { code?: string }).code
          : undefined) || String(captureValue);
      const isEstimated = captureCode === "estimated" || captureCode === "False";
      const oldEblValue = isEstimated
        ? Number(form?.values?.estimatedBloodLossEstimated) || 0
        : Number(form?.values?.estimatedBloodLossQuantitative) || 0;

      setPreviousBloodLossValue(oldEblValue);
    }
  }, [isEditing, cumulativeBloodLoss, currentBloodLoss, previousBloodLossValue, form?.values]);

  // Calculate adjusted cumulative blood loss
  const adjustedCumulativeBloodLoss = useMemo(() => {
    if (isEditing) {
      // If editing, subtract the previous value and add the current value
      const result = cumulativeBloodLoss - previousBloodLossValue + currentBloodLoss;
      return result;
    } else {
      // If new entry, add current blood loss to cumulative
      const result = cumulativeBloodLoss + currentBloodLoss;
      return result;
    }
  }, [cumulativeBloodLoss, currentBloodLoss, previousBloodLossValue, isEditing]);

  // Update the form value when cumulative blood loss changes
  useEffect(() => {
    if (form && adjustedCumulativeBloodLoss !== undefined) {
      form.setFieldValue(fieldPath, adjustedCumulativeBloodLoss);
    }
  }, [adjustedCumulativeBloodLoss, form, fieldPath]);

  // Set initial form value when cumulative blood loss is loaded
  useEffect(() => {
    if (!hasSeededFormRef.current && form && cumulativeBloodLoss !== undefined && !isLoading) {
      form.setFieldValue(fieldPath, cumulativeBloodLoss);
      hasSeededFormRef.current = true;
    }
  }, [cumulativeBloodLoss, form, fieldPath, isLoading]);

  // Update cumulative blood loss in real-time as user types
  useEffect(() => {
    if (form && !isLoading && currentBloodLoss >= 0) {
      if (isEditing) {
        // For editing, use the adjusted calculation (subtract old + add new)
        const newCumulative = cumulativeBloodLoss - previousBloodLossValue + currentBloodLoss;
        form.setFieldValue(fieldPath, newCumulative);
      } else {
        // For new entries, use simple addition
        const newCumulative = cumulativeBloodLoss + currentBloodLoss;
        form.setFieldValue(fieldPath, newCumulative);
      }
    }
  }, [
    currentBloodLoss,
    cumulativeBloodLoss,
    previousBloodLossValue,
    isEditing,
    form,
    fieldPath,
    isLoading,
  ]);

  // Server cumulative only; form field stays derived via adjustedCumulativeBloodLoss + existing effects
  // (avoids double-applying currentBloodLoss alongside the subscription and typing effects).
  useEffect(() => {
    if (
      !shouldUseLaborEncounterLiveCbl ||
      laborEncounterLive?.liveCumulativeBloodLoss === undefined
    ) {
      return;
    }
    hasAppliedLiveCumulativeRef.current = true;
    setCumulativeBloodLoss(laborEncounterLive.liveCumulativeBloodLoss);
  }, [shouldUseLaborEncounterLiveCbl, laborEncounterLive?.liveCumulativeBloodLoss]);

  // Subscribe to cumulative blood loss observations for real-time updates (non–labor-encounter routes)
  useSubscription(
    shouldUseLaborEncounterLiveCbl || !patientId || !finalEncounterId
      ? ""
      : `Observation?subject=Patient/${patientId}&encounter=Encounter/${finalEncounterId}&code=${Systems.LOINC}|${LOINCCodes.CumulativeBloodLoss}&status:not=cancelled&_sort=-_lastUpdated`,
    (bundle) => {
      if (!patientId || !finalEncounterId) return;

      const observations = getResourcesFromBundle("Observation", bundle) as Observation[];

      if (observations.length > 0) {
        const latestCblObservation = observations[0]; // Most recent observation
        const newCumulativeValue = latestCblObservation.valueQuantity?.value || 0;

        // Update the cumulative blood loss state
        setCumulativeBloodLoss(newCumulativeValue);

        // Update the form field with the new cumulative value
        if (form) {
          // The newCumulativeValue is already the final cumulative value from the observation
          // We need to add the current EBL value to show the total that will be after submission
          const newCumulative = newCumulativeValue + currentBloodLossRef.current;
          form.setFieldValue(fieldPath, newCumulative);
        }
      }
    },
    {
      onError: (error) => {
        console.error("Cumulative blood loss subscription error:", error);
      },
    },
  );

  if (isLoading) {
    return (
      <QuestionnaireNumberInputField
        form={form}
        name={name}
        label={label}
        error={error}
        readOnly={true}
        minRange={minRange}
        maxRange={maxRange}
        fieldPath={fieldPath}
        placeholder="Loading..."
        data-testid={dataTestId}
        rightSection={rightSection}
        withAsterisk={withAsterisk}
        allowDecimal={allowDecimal}
        formFieldName={formFieldName}
        allowNegative={allowNegative}
        questionnaireItem={questionnaireItem}
      />
    );
  }

  return (
    <QuestionnaireNumberInputField
      form={form}
      name={name}
      label={label}
      error={error}
      readOnly={true}
      minRange={minRange}
      maxRange={maxRange}
      fieldPath={fieldPath}
      data-testid={dataTestId}
      rightSection={rightSection}
      withAsterisk={withAsterisk}
      allowDecimal={allowDecimal}
      formFieldName={formFieldName}
      allowNegative={allowNegative}
      questionnaireItem={questionnaireItem}
      placeholder={adjustedCumulativeBloodLoss.toString()}
    />
  );
}
